import React from 'react';
import { motion } from 'framer-motion';
import { Download, Smartphone, CheckCircle, Zap, TrendingUp, Shield, Star } from 'lucide-react';

const DownloadSection = () => {
  const features = [
    { icon: CheckCircle, text: 'Free to download' },
    { icon: Zap, text: 'Instant results' },
    { icon: TrendingUp, text: 'Best market rates' },
    { icon: Shield, text: 'Secure platform' }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 md:w-96 md:h-96 bg-primary/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 md:w-72 md:h-72 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }}></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(76,175,80,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(76,175,80,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
      </div>

      <div className="w-full max-w-[85%] mx-auto px-4 md:px-6 relative z-10">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full text-primary text-sm font-medium mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Get The App
          </motion.span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 md:mb-6 text-gray-900">
            Download{' '}
            <span className="text-gradient">SMR Online</span>{' '}
            Now
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Get started with SMR Online today. Download the app to check live results and play online Matka.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Features List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-lg border border-gray-100 card-hover"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-gray-700 text-sm md:text-base">{feature.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Download Button */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <a
                href="https://www.smronline.org/smronline.apk"
                download
                className="group bg-gradient-primary text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:shadow-lg hover:shadow-primary/40 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 pulse-glow"
              >
                <Download className="w-6 h-6" />
                <span>Download Now</span>
              </a>
            </motion.div>

            {/* Rating */}
            <motion.div
              className="flex items-center gap-4 mt-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <span className="text-gray-600 text-sm">4.8 rating from 10,000+ users</span>
            </motion.div>
          </motion.div>

          {/* Right Content - Phone Mockup */}
          <motion.div
            className="flex justify-center lg:justify-end mt-8 lg:mt-0"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-radial from-primary/20 via-primary/5 to-transparent rounded-[3rem] blur-2xl animate-pulse"></div>

              {/* Phone Frame */}
              <div className="relative w-64 h-[480px] md:w-72 md:h-[540px] lg:w-80 lg:h-[600px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[2.5rem] md:rounded-[3rem] p-1.5 md:p-2 shadow-2xl green-glow">
                <div className="w-full h-full bg-white rounded-[2rem] md:rounded-[2.5rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
                    <span className="text-xs text-gray-600">9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 border border-primary rounded-sm">
                        <div className="w-3/4 h-full bg-primary rounded-sm"></div>
                      </div>
                    </div>
                  </div>

                  {/* Download Complete Screen */}
                  <div className="flex flex-col items-center justify-center h-full p-6 -mt-8 bg-gray-50">
                    <motion.div
                      className="w-24 h-24 bg-gradient-primary rounded-full flex items-center justify-center mb-6 shadow-lg shadow-primary/30"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <CheckCircle className="w-12 h-12 text-white" />
                    </motion.div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">Download Complete</h3>
                    <p className="text-gray-500 text-sm text-center mb-6">
                      SMR Online is ready to use
                    </p>

                    <div className="bg-white rounded-xl p-4 w-full shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="SMR" className="w-12 h-12 rounded-xl" />
                        <div>
                          <div className="font-semibold text-gray-900">SMR Online</div>
                          <div className="text-xs text-gray-500">v2.0.1 • 15MB</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 w-full">
                      <div className="bg-gradient-primary text-white py-3 rounded-xl text-center font-semibold shadow-lg">
                        Open App
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;
